/**
 * TreasureScene - Find random rewards and loot
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { getUpgradeOffers, getUpgradeColor, Upgrade } from '../game/upgrades/UpgradeLibrary';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { PieceType } from '../types';
import { AudioManager } from '../game/AudioManager';
import { T, INK, makeOptionCard, paintBackground, fadeInScene } from '../constants/Theme';

export class TreasureScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;
  private treasures: TreasureReward[] = [];

  constructor() {
    super({ key: SCENES.TREASURE });
  }

  init(): void {
    this.gameStateManager = GameStateManager.getInstance();
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const centerX = WIDTH / 2;

    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);
    AudioManager.getInstance().init(this);

    const title = this.add.text(centerX, 90, 'TREASURE', T.title({ color: INK.gold }));
    title.setOrigin(0.5);
    title.setLetterSpacing(3);

    const subtitle = this.add.text(centerX, 152, 'Choose one reward to claim', T.body());
    subtitle.setOrigin(0.5);

    // Generate treasure rewards
    this.generateTreasureRewards();

    // Display treasure options
    let yOffset = 290;
    const spacing = 178;

    for (const treasure of this.treasures) {
      this.createTreasureOption(centerX, yOffset, treasure);
      yOffset += spacing;
    }
  }

  private generateTreasureRewards(): void {
    const rewardTypes = ['gold', 'upgrade', 'piece'];
    const selectedType = rewardTypes[Math.floor(Math.random() * rewardTypes.length)];

    switch (selectedType) {
      case 'gold':
        // 2 gold options
        this.treasures = [
          {
            type: 'gold',
            name: 'Large Gold Pile',
            description: 'Gain 150 gold',
            amount: 150,
            icon: '💰',
            color: '#f1c40f',
          },
          {
            type: 'gold',
            name: 'Small Gold Pile',
            description: 'Gain 75 gold',
            amount: 75,
            icon: '⚄',
            color: '#f39c12',
          },
        ];
        break;

      case 'upgrade':
        // 2 random upgrades
        const upgrades = getUpgradeOffers(2, this.gameStateManager.getOfferContext('normal'));
        this.treasures = upgrades.map(upgrade => ({
          type: 'upgrade',
          name: upgrade.name,
          description: upgrade.description,
          upgrade: upgrade,
          icon: '⭐',
          color: getUpgradeColor(upgrade.rarity),
        }));
        break;

      case 'piece':
        // 2 piece options
        const piece1 = this.getRandomPiece();
        const piece2 = this.getRandomPiece();
        this.treasures = [
          {
            type: 'piece',
            name: `${this.getPieceName(piece1)} Recruit`,
            description: `Add a ${this.getPieceName(piece1)} to your army`,
            pieceType: piece1,
            icon: '♟',
            color: '#2ecc71',
          },
          {
            type: 'piece',
            name: `${this.getPieceName(piece2)} Recruit`,
            description: `Add a ${this.getPieceName(piece2)} to your army`,
            pieceType: piece2,
            icon: '♟',
            color: '#27ae60',
          },
        ];
        break;
    }
  }

  private createTreasureOption(x: number, y: number, treasure: TreasureReward): void {
    const accent = parseInt(treasure.color.replace('#', '0x'));
    const w = 640, h = 150;
    makeOptionCard(this, x, y, treasure.name, treasure.description, {
      width: w, height: h, icon: treasure.pieceType ? undefined : treasure.icon,
      accent, accentInk: treasure.color,
      onClick: () => this.claimTreasure(treasure),
    });

    // Piece sprite for piece rewards (sits where the icon would)
    if (treasure.pieceType) {
      const sprite = this.add.sprite(x - w / 2 + 70, y, `w${treasure.pieceType}`);
      sprite.setDisplaySize(86, 86);
      sprite.setDepth(101);
    }

    if (treasure.upgrade) {
      const badge = this.add.text(x + w / 2 - 24, y - h / 2 + 16, treasure.upgrade.rarity.toUpperCase(),
        T.micro({ color: treasure.color }));
      badge.setOrigin(1, 0);
      badge.setLetterSpacing(2);
      badge.setDepth(102);
    }
  }

  private claimTreasure(treasure: TreasureReward): void {
    let message = '';
    let color = treasure.color;

    switch (treasure.type) {
      case 'gold':
        this.gameStateManager.addGold(treasure.amount!);
        message = `Found ${treasure.amount} Gold!`;
        break;

      case 'upgrade': {
        this.gameStateManager.applyUpgrade(treasure.upgrade!);
        message = `Acquired: ${treasure.name}!`;
        break;
      }

      case 'piece':
        // Add piece to army (excluding kings, which shouldn't be in treasure anyway)
        if (treasure.pieceType && treasure.pieceType !== 'k') {
          this.gameStateManager.addPiece(treasure.pieceType);
        }
        message = `Recruited: ${this.getPieceName(treasure.pieceType!)}!`;
        break;
    }

    this.gameStateManager.saveGame();

    // Show acquisition feedback
    this.showTreasureFeedback(message, color);

    // Proceed after delay
    this.time.delayedCall(2500, () => {
      this.scene.start(SCENES.MAP);
    });
  }

  private showTreasureFeedback(message: string, color: string): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const centerX = WIDTH / 2;
    const centerY = HEIGHT / 2;

    // Dim background
    const overlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x0a0f1c, 0.82);
    overlay.setOrigin(0);
    overlay.setDepth(300);

    // Sparkle effects
    for (let i = 0; i < 10; i++) {
      const sparkle = this.add.text(
        centerX + (Math.random() - 0.5) * 400,
        centerY + (Math.random() - 0.5) * 300,
        '✨',
        {
          fontSize: '32px',
        }
      );
      sparkle.setOrigin(0.5);
      sparkle.setDepth(301);
      sparkle.setAlpha(0);

      // Sparkle animation
      this.tweens.add({
        targets: sparkle,
        alpha: 1,
        scale: 1.5,
        duration: 500,
        delay: i * 100,
        yoyo: true,
        onComplete: () => sparkle.destroy(),
      });
    }

    // Feedback text
    const feedbackText = this.add.text(centerX, centerY, message, T.title({
      color, align: 'center', wordWrap: { width: WIDTH - 100 },
    }));
    feedbackText.setOrigin(0.5);
    feedbackText.setDepth(302);

    // Fade in and scale effect
    feedbackText.setAlpha(0);
    feedbackText.setScale(0.5);
    this.tweens.add({
      targets: feedbackText,
      alpha: 1,
      scale: 1.2,
      duration: 500,
      ease: 'Back.easeOut',
    });
  }

  private getRandomPiece(): PieceType {
    const pieces: PieceType[] = ['p', 'n', 'b', 'r'];
    // Weighted random: pawns more common, rooks less common
    const weights = [5, 3, 3, 1]; // p, n, b, r
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    let random = Math.random() * totalWeight;

    for (let i = 0; i < pieces.length; i++) {
      random -= weights[i];
      if (random <= 0) {
        return pieces[i];
      }
    }

    return 'p'; // Fallback
  }

  private getPieceName(type: string): string {
    const names: Record<string, string> = {
      p: 'Pawn',
      n: 'Knight',
      b: 'Bishop',
      r: 'Rook',
      q: 'Queen',
      k: 'King',
    };
    return names[type] || 'Unknown';
  }
}

interface TreasureReward {
  type: 'gold' | 'upgrade' | 'piece';
  name: string;
  description: string;
  icon: string;
  color: string;
  amount?: number; // For gold
  upgrade?: Upgrade; // For upgrades
  pieceType?: PieceType; // For pieces
}
